import React, { useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { LogOut, ShieldCheck } from "lucide-react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

export function AuthCallback() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    const hash = window.location.hash;
    const match = hash.match(/session_id=([^&]+)/);
    const sessionId = match ? match[1] : null;
    if (!sessionId) { navigate("/login", { replace: true }); return; }
    (async () => {
      try {
        const r = await api.post("/auth/session", { session_id: sessionId });
        setUser(r.data);
        window.history.replaceState(null, "", "/profile");
        navigate("/profile", { replace: true, state: { user: r.data } });
      } catch {
        navigate("/login", { replace: true });
      }
    })();
  }, [navigate, setUser]);

  return (
    <main className="min-h-[60vh] flex items-center justify-center">
      <div className="text-center">
        <div className="w-10 h-10 mx-auto rounded-full border-[3px] border-[#3F4CE0] border-t-transparent animate-spin" />
        <p className="mt-4 text-[14px] text-[#1A1914]/60">Signing you in…</p>
      </div>
    </main>
  );
}

export function Profile() {
  const { user, loading, login, logout } = useAuth();
  const navigate = useNavigate();

  if (loading) return <main className="min-h-[60vh] flex items-center justify-center"><div className="w-10 h-10 rounded-full border-[3px] border-[#3F4CE0] border-t-transparent animate-spin" /></main>;

  if (!user) {
    return (
      <main className="min-h-[60vh] flex items-center justify-center px-5" data-testid="profile-page">
        <div className="text-center max-w-md">
          <h1 className="font-display font-bold text-[30px]">You're not signed in.</h1>
          <p className="mt-2 text-[14.5px] text-[#1A1914]/60">Sign in with Google to save assessments and chat history with Sam.</p>
          <button onClick={login} data-testid="profile-login-btn" className="mt-6 px-6 py-3 text-[14.5px] font-semibold text-white bg-[#3F4CE0] hover:bg-[#3341C6] rounded-xl rp-btn">Continue with Google</button>
        </div>
      </main>
    );
  }

  return (
    <main className="max-w-[720px] mx-auto px-5 py-16" data-testid="profile-page">
      <div className="bg-white border border-[#E7E0D2] rounded-2xl p-8">
        <div className="flex items-center gap-4">
          {user.picture ? (
            <img src={user.picture} alt={user.name} className="w-14 h-14 rounded-full border border-[#E7E0D2]" referrerPolicy="no-referrer" />
          ) : (
            <div className="w-14 h-14 rounded-full bg-[#3F4CE0] text-white font-display font-bold text-[22px] flex items-center justify-center">{(user.name || user.email)[0]?.toUpperCase()}</div>
          )}
          <div>
            <h1 className="font-display font-bold text-[26px]" data-testid="profile-name">{user.name || "Risk professional"}</h1>
            <p className="text-[14px] text-[#1A1914]/55" data-testid="profile-email">{user.email}</p>
          </div>
        </div>
        <div className="mt-6 flex items-center gap-2 px-4 py-3 rounded-xl bg-[#12B76A]/5 border border-[#12B76A]/20 text-[13.5px]">
          <ShieldCheck size={15} className="text-[#0E9355]" /> Signed in with Google · sessions last 7 days
        </div>
        <div className="mt-6 flex flex-wrap gap-3">
          <Link to="/admin" className="px-5 py-2.5 text-[14px] font-semibold text-white bg-[#1A1914] hover:bg-black rounded-xl rp-btn">Open admin console</Link>
          <button onClick={async () => { await logout(); navigate("/"); }} data-testid="profile-logout-btn" className="inline-flex items-center gap-2 px-5 py-2.5 text-[14px] font-semibold border border-[#E7E0D2] hover:border-[#E5484D]/50 hover:text-[#DC2626] rounded-xl rp-btn"><LogOut size={15} /> Log out</button>
        </div>
      </div>
    </main>
  );
}
